import { Fragment, useEffect, useState, useMemo } from "react";
import { useMatch, useNavigate } from "react-router";
import { findIndex, first, filter, map, find, some } from 'lodash';
import { Paths } from "../routes/Routes";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { useLazyQuery } from "@apollo/client";
import { GET_USERS } from "../../graphQL/queries";
import { User } from "../../models/User";
import { NoContentWrapper, displayAcknowledgeDate, renderPeriod } from "../../helpers/renderHelper";
import LoadingSpinner from "./LoadingSpinner";
import DriverCard from "./DriverCard";
import { buildGroupDrivers, buildGroupDriversWithIncident } from "../../helpers/managerHelper";
import { IIncidentDetails, buildIncidents, isPlaceholderIncident } from "../../helpers/incidentHelper";
import IncidentCard from "./IncidentCard";
import { IDLE_TIME_INCIDENT_NAME } from '../../constants';
import { navigatToDriverIncidentDetail } from "../../helpers/navigationHelper";
import { setUsers } from "../../redux/appStateSlice";
import { incrementBackNavigation } from "../../redux/navigationSlice";
import { AppMessages } from "../../strings";

const IncidentDetail = () => {

	const managerDriverIncidentMatch = useMatch(Paths.MANAGER_DRIVER_INCIDENT_DETAIL);
	const driverIncidentMatch = useMatch(Paths.DRIVER_INCIDENT_DETAIL);

	const dispatch = useAppDispatch();
	const navigate = useNavigate()

	const currentUser = useAppSelector(state => state.appState.currentUser);

	const currentGroupID = parseInt(managerDriverIncidentMatch?.params.groupid ?? "-1")

	var currentIncidentName = managerDriverIncidentMatch?.params.name
	if (currentUser?.isInDriverRole === true) {
		currentIncidentName = driverIncidentMatch?.params.name
	}

	const [selectedIIncident, setSelectedIIncident] = useState<IIncidentDetails|undefined>()

	const currentOrganization = useAppSelector(state => state.appState.currentOrganization);
	const [currentDriver, setCurrentDriver] = useState<User|undefined>()
	const allDrivers = useAppSelector(state => state.appState.groupUsers[currentGroupID]?.users)
	const filteredDrivers = buildGroupDrivers(allDrivers);
	const drivers = currentIncidentName ? buildGroupDriversWithIncident(filteredDrivers, currentIncidentName) : [];

	const allUIIncidents = useMemo(() => {
		if (currentUser?.isInDriverRole === true) {
			return buildIncidents(currentUser?.scorecard?.incidents);
		} else {
			return buildIncidents(currentDriver?.scorecard?.incidents);
		}
	}, [currentUser?.isInDriverRole, currentUser?.scorecard?.incidents, currentDriver?.scorecard?.incidents]);

	const selectedIncidents = useMemo(() => {
		const source = currentUser?.isInDriverRole === true ? currentUser?.scorecard?.incidents : currentDriver?.scorecard?.incidents;
		return filter(source, inc => inc.name === selectedIIncident?.name);
	}, [currentUser?.isInDriverRole, currentUser?.scorecard?.incidents, currentDriver?.scorecard?.incidents, selectedIIncident?.name]);

	// List the real occurrences (with their dates); placeholders (records the scorecard
	// counted but whose detail could not be found) are rolled up into a single
	// "details missing" line shown once, regardless of how many placeholders exist.
	const currentIncidents = useMemo(() => filter(selectedIncidents, inc => !isPlaceholderIncident(inc)), [selectedIncidents]);
	const hasMissingIncidentDetails = useMemo(() => some(selectedIncidents, isPlaceholderIncident), [selectedIncidents]);

	const [getDriver, driverResponses ] = useLazyQuery(GET_USERS);

	const [getDrivers, getDriversResponses ] = useLazyQuery(GET_USERS);

	useEffect(()=> {
		if (currentUser?.isInDriverRole === false) {
			getDriver({
				variables: {
					userID: parseInt( managerDriverIncidentMatch?.params.id ?? "-1"),
					onlyDrivers: true,
					startDate: import.meta.env.REACT_APP_MANAGER_START_DATE ? import.meta.env.REACT_APP_MANAGER_START_DATE : null ,
					endDate: import.meta.env.REACT_APP_MANAGER_END_DATE ? import.meta.env.REACT_APP_MANAGER_END_DATE : null,
					prevStartDate: import.meta.env.REACT_APP_MANAGER_PREV_START_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_START_DATE : null,
					prevEndDate: import.meta.env.REACT_APP_MANAGER_PREV_END_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_END_DATE : null
				}
			});
		} else {
			const firstIncident = find(allUIIncidents, inc => inc.name === currentIncidentName)
			if (firstIncident) {
				setSelectedIIncident(firstIncident)
			}
		}
	},[currentUser?.isInDriverRole, currentIncidentName, getDriver, allUIIncidents, managerDriverIncidentMatch?.params.id]);

	useEffect(() => {
		if (driverResponses?.data) {
			// console.log('driverResponses?.data = ', driverResponses?.data)
			const driver = first(driverResponses?.data.users)
			if (driver) {
				setCurrentDriver(driver)
				getDrivers({
					variables: {
						groupID: currentGroupID,
						onlyDrivers: true,
						startDate: import.meta.env.REACT_APP_MANAGER_START_DATE ? import.meta.env.REACT_APP_MANAGER_START_DATE : null ,
						endDate: import.meta.env.REACT_APP_MANAGER_END_DATE ? import.meta.env.REACT_APP_MANAGER_END_DATE : null,
						prevStartDate: import.meta.env.REACT_APP_MANAGER_PREV_START_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_START_DATE : null,
						prevEndDate: import.meta.env.REACT_APP_MANAGER_PREV_END_DATE ? import.meta.env.REACT_APP_MANAGER_PREV_END_DATE : null
					}
				})

				const allUIIncidents = buildIncidents(driver?.scorecard?.incidents)
				const firstIncident = find(allUIIncidents, inc => inc.name === currentIncidentName)
				if (firstIncident) {
					setSelectedIIncident(firstIncident)
				}
			}
		}
	},[driverResponses?.data, currentIncidentName, getDrivers, currentGroupID])

	useEffect(()=>{
		if (getDriversResponses?.data) {
			dispatch(setUsers({groupId: currentGroupID, groupUsers:getDriversResponses?.data}))
		}
	},[getDriversResponses?.data, currentGroupID, dispatch])

	function navigateToDriver(e, user) {
		e.preventDefault();
		navigatToDriverIncidentDetail(user, Paths.MANAGER_DRIVER_INCIDENT_DETAIL, currentGroupID, currentIncidentName, navigate, dispatch)
		dispatch(incrementBackNavigation())
	}

	function setCurrentIIncident(iincident) {
		setSelectedIIncident(iincident)
		navigatToDriverIncidentDetail(currentUser, Paths.MANAGER_DRIVER_INCIDENT_DETAIL, currentGroupID, iincident.name, navigate, dispatch)
		dispatch(incrementBackNavigation())
	}

	const currentIndex = findIndex(drivers, driver => driver.userID === currentDriver?.userID)
	const isFirst = drivers.length ? currentIndex === 0 : true;
	const isLast =  drivers.length ? currentIndex === (drivers?.length - 1) : true;

	return (
		<Fragment>
			{ driverResponses.loading && <LoadingSpinner />}
			<div className="container" >
				{ !currentUser?.isInDriverRole &&
					<div className="row" >
						<div className="col-12">
							<div className="card">
								<div className="card-body align-items-center" style={{padding: "0.35rem", marginBottom:-6}}>
									<DriverCard  user={ currentDriver} isFirst={isFirst} isLast={isLast} carouselMode={true} navigateLeft={ e => navigateToDriver(e, drivers[currentIndex - 1]) } navigateRight={ e => navigateToDriver(e, drivers[currentIndex + 1]) } />
								</div>
							</div>
						</div>
					</div>
				}
				
				<div className="row">
					<div className="col-12">
						<div className="card">
							<div className="card-body align-items-center" style={{padding: "0.35rem", marginBottom:-6}}>
								<IncidentCard currentIncident={selectedIIncident} incidents={allUIIncidents} carouselMode={true} setCurrentIIncident={setCurrentIIncident}/>
							</div>
						</div>
					</div>
				</div>
				{renderPeriod(currentOrganization)}
				<div className="row">
					<div className="col-12">
						<div className="container" >
								<div className="row g-0 p-0">
									<div className="col col-12">
											<div className="row g-0" style={{margin: "0", padding: "0.35rem",  color:'white'}}>
												<div  style={{width: "100%"}}>
													<NoContentWrapper items={selectedIncidents} message={currentUser?.scorecard?.currentScore ? AppMessages.noIncidents : AppMessages.noDriverScore} showIcon={currentUser?.scorecard?.currentScore ? true : false}>
														<div className="row g-0" style={{height:45}}>
															<div className="col-12 fs-5 mb-sm-0 fw-bold" style={{marginBottom: "0", paddingBottom: "0", }}>{selectedIIncident?.name === IDLE_TIME_INCIDENT_NAME ? "Details" : "Occurrences"}</div>
															<hr className="hr" style={{color:'white', opacity:1.0, marginTop: "0.35rem"}} />
														</div>
														{ map(currentIncidents,(incident,index) => {
															return (
																<div className="row g-0" key={`incident-occurence-${index}`} style={{height:40}}>
																	<div className="col-12 fs-6 mt-sm-0 g-0" style={{marginBottom: "0", paddingBottom: "0"}} >{incident.name === IDLE_TIME_INCIDENT_NAME ? incident.details : displayAcknowledgeDate(incident.startTime)}</div>
																	<hr className="hr" style={{color:'white', opacity:1.0, marginTop: "0.35rem"}} />
																</div>
															)
														})}
														{ hasMissingIncidentDetails &&
															<div className="row g-0" style={{height:40}}>
																<div className="col-12 fs-6 fst-italic mt-sm-0 g-0" style={{marginBottom: "0", paddingBottom: "0"}} data-testid="missing-incidents-notice">{AppMessages.missingIncidentDetails}</div>
																<hr className="hr" style={{color:'white', opacity:1.0, marginTop: "0.35rem"}} />
															</div>
														}
													</NoContentWrapper>
												</div>
											</div>
									</div>
								</div>
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	)
}

export default IncidentDetail;